export const BOOK_FILE_FIELD_KEYS = ["book_cover", "book_file"];

export const BOOK_UPLOAD_STEPS = {
	SAVING_DETAILS: "saving-details",
	UPLOADING_FILES: "uploading-files",
	ACTIVATING: "activating",
};

export const splitBookFileFields = (body) => {
	const files = {};
	const metadata = { ...body };

	BOOK_FILE_FIELD_KEYS.forEach((key) => {
		if (metadata[key]) {
			files[key] = metadata[key];
			delete metadata[key];
		}
	});

	return { files, metadata };
};

export const hasBookFileFields = (files) =>
	BOOK_FILE_FIELD_KEYS.some((key) => Boolean(files?.[key]));

export const pickBookFiles = (values) => {
	const files = {};

	BOOK_FILE_FIELD_KEYS.forEach((key) => {
		if (values?.[key]) {
			files[key] = values[key];
		}
	});

	return files;
};

export const hasSelectedFiles = (values) => Boolean(values?.book_cover || values?.book_file);

const ACCEPTED_BOOK_FILE_TYPES = ["application/pdf"];
const ACCEPTED_BOOK_FILE_EXTENSIONS = ["pdf"];

export const isAcceptedBookFile = (file) => {
	if (!file) return true;

	const extension = file.name?.split(".").pop()?.toLowerCase();
	return (
		ACCEPTED_BOOK_FILE_TYPES.includes(file.type) ||
		ACCEPTED_BOOK_FILE_EXTENSIONS.includes(extension)
	);
};

export const buildFormData = (payload) => {
	const formData = new FormData();
	Object.keys(payload).forEach((key) => {
		const value = payload[key];

		if (Array.isArray(value)) {
			if (value.length === 0) {
				// Explicit empty array so the backend can clear relations (e.g. tags).
				formData.append(`${key}[]`, "");
				return;
			}

			value.forEach((item) => {
				if (item != null && item !== "") {
					formData.append(`${key}[]`, item);
				}
			});
			return;
		}

		if (value != null && value !== "") {
			formData.append(key, value);
		}
	});
	return formData;
};

export const uploadBookFiles = async (instance, bookId, files) => {
	const payload = pickBookFiles(files);

	if (!hasBookFileFields(payload)) {
		return null;
	}

	// PHP does not populate $_FILES for PATCH; use POST + method spoofing.
	return instance.post(`/books/files/${bookId}`, buildFormData(payload));
};

export const activateBook = async (instance, bookId) => {
	return instance.patch(`/books/change-status/${bookId}`, { is_active: 1 });
};

export const isActiveRequested = (value) => value === 1 || value === "1" || value === true;

export const formatFileSize = (bytes) => {
	if (!bytes) return "";
	const mb = bytes / 1024 / 1024;
	if (mb < 1) {
		return `${Math.round(bytes / 1024)} KB`;
	}
	return `${mb.toFixed(1)} MB`;
};

export const getUploadFileSteps = ({ activateAfterUpload = false } = {}) => {
	const steps = [
		{
			key: BOOK_UPLOAD_STEPS.UPLOADING_FILES,
			label: "Uploading cover & book file",
		},
	];

	if (activateAfterUpload) {
		steps.push({
			key: BOOK_UPLOAD_STEPS.ACTIVATING,
			label: "Activating book",
		});
	}

	return steps;
};

export const getSubmitButtonLabel = ({ isLoading, uploadStep, mode = "create" }) => {
	if (isLoading) {
		if (uploadStep === BOOK_UPLOAD_STEPS.UPLOADING_FILES) {
			return "Uploading files…";
		}
		if (uploadStep === BOOK_UPLOAD_STEPS.ACTIVATING) {
			return "Activating book…";
		}
		return mode === "create" ? "Creating book…" : "Saving changes…";
	}

	return mode === "create" ? "Create book" : "Save changes";
};

export const getUploadFilesButtonLabel = ({ isLoading, uploadStep, activateAfterUpload }) => {
	if (isLoading) {
		if (uploadStep === BOOK_UPLOAD_STEPS.ACTIVATING) {
			return "Activating…";
		}
		return "Uploading…";
	}

	return activateAfterUpload ? "Upload files & activate" : "Upload files";
};
